import { Button, VText } from '@vake/ui-kit';
import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import styled from 'styled-components';
import { useAuth } from '../../../auth/AuthProvider';
import { orderPathfinderSubscription, useIsAdmin } from '../../../requests/reports';
import constants from '../../../shared/constants';
import { InputField, InputFieldTW } from '../../../shared/styles/InputFields';
import { AbsoluteSpinner } from '../../../shared/styles/Spinner';
import { useInput } from '../components/useInput';
import { SimpleDateSelector } from '../interactiveReport/reportInfo/DateSelector';

const OrderReportWrapper = styled.div`
    width: calc(${constants.sizes.container} - 800px);
    max-width: calc(100% - 100px);
    display: flex;
    position: relative;
    flex-direction: column;
    border-radius: 4px;
    background: radial-gradient(42.95% 171.27% at 86.22% 0%, rgba(0, 161, 255, 0.65) 0%, rgba(0, 161, 255, 0.5) 100%);

    margin: auto;
    padding: 24px;
    margin-top: 300px;
    margin-bottom: 100px;
`;

const InputWrapper = styled.div`
    display: flex;
    justify-content: space-between;
    flex-direction: column;
    ${InputField} {
        width: calc(100% - 50px);
    }
    margin-left: 10%;
    margin-right: 10%;
    margin- @media screen and (max-width: ${constants.breakpoints.mobile}) {
        flex-direction: column;
    }
`;

const InputRow = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 15px;
    label {
        margin-right: 20px;
    }
`;

const StyledInputField = styled(InputField)`
    line-height: normal;
    line-height: 10px;
`;

const ButtonWrapper = styled.div`
    display: flex;
    justify-content: center;
`;

const ErrorMessageContainer = styled.div`
    background-color: #c95b5b;
    border-radius: 5px;
    padding: 10px;
    text-align: center;
`;

//Validation:
const required = (text: string, fieldName: string) => {
    if (text) {
        return '';
    }
    return `${fieldName} is required`;
};

const validMmsi = (value: string, fieldName: string) => {
    const onlyNumbersRegex = /^\d+$/;
    if (onlyNumbersRegex.test(value) && value.length === 9) {
        return '';
    }
    return `${fieldName} is not a valid MMSI`;
};

const xMonthsAgo = (months: number): Date => {
    const date = new Date();
    date.setMonth(date.getMonth() - months);
    return date;
};

const getDate = (date: Date | null) => {
    if (date) return new Date(date.getFullYear(), date.getMonth(), date.getDate());
    return date;
};

// TODO: Prevent user to set fromDate later than toDate
const OrderPathfinderReport: FC = () => {
    const { token } = useAuth();
    const { isLoading, isFetched } = useIsAdmin(token);
    const [searchParams] = useSearchParams();
    const [orderSubscriptionSuccess, setOrderSubscriptionSuccess] = useState<boolean | undefined>(undefined);
    const [reportPath, setReportPath] = useState<string | undefined>(undefined);
    const [fromDate, setFromDate] = useState<Date | null>(xMonthsAgo(5));
    const [toDate, setToDate] = useState<Date | null>(new Date());
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const [reportName, setReportName] = useState<string>(searchParams.get('name') || '');

    useEffect(() => {
        document.title = 'Order Pathfinder';
    }, []);

    const mmsi = useInput(searchParams.get('mmsi') || '', 'MMSI', [required, validMmsi]);

    const isTimeRangeValid = useMemo(() => {
        if (fromDate && toDate) {
            const timeDifference = toDate.getTime() - fromDate.getTime();
            if (timeDifference > 34992000000) {
                // 1000 * 60 * 60 * 24 * 30 * 13,5 is about 13 months
                setErrorMessage('Time period has to be shorter than 13 months.');
                return false;
            }
            if (timeDifference < 4924800000) {
                // 1000 * 60 * 60 * 24 * 57 is 57 days
                setErrorMessage('Time period has to be longer than 2 months days.');
                return false;
            }
            setErrorMessage(null);
            return true;
        }
        return false;
    }, [fromDate, toDate]);

    const addDayToDate = (chosenDate: Date | null) => {
        if (chosenDate) {
            chosenDate.setDate(chosenDate.getDate() + 1);
            setToDate(chosenDate);
        }
    };

    const allFieldsValid = useMemo(() => {
        return Boolean(
            (mmsi.validation.touched || searchParams.has('mmsi')) && !mmsi.validation.errors.length && isTimeRangeValid
        );
    }, [mmsi.validation.touched, mmsi.validation.errors.length, searchParams, isTimeRangeValid]);

    const resetFields = useCallback(() => {
        mmsi.reset();
        setReportName('');
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const orderSubscriptionCallback = (requestSuccess: boolean, reportPath: string | undefined) => {
        if (requestSuccess) resetFields();
        setOrderSubscriptionSuccess(requestSuccess);
        setReportPath(reportPath);
    };

    const submitLoginForm = (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        if (allFieldsValid) {
            orderPathfinderSubscription(
                mmsi.value,
                getDate(fromDate)!,
                getDate(toDate),
                reportName,
                token,
                (orderSubscriptionSuccess: boolean, reportPath: string | undefined) =>
                    orderSubscriptionCallback(orderSubscriptionSuccess, reportPath)
            );
        }
    };

    return (
        <OrderReportWrapper>
            {isLoading && <AbsoluteSpinner />}
            {isFetched && (
                <div className="text-center">
                    <VText variant="H1">Order VAKE Pathfinder</VText>
                    <form onSubmit={(e) => submitLoginForm(e)}>
                        <InputWrapper>
                            <InputRow>
                                <InputFieldTW
                                    id="mmsi"
                                    type="mmsi"
                                    placeholder="MMSI"
                                    error={mmsi.validation.errors.length > 0}
                                    {...mmsi.bind}
                                />
                            </InputRow>
                            <InputRow>
                                <InputFieldTW
                                    id="report_name"
                                    type="report_name"
                                    placeholder="Report Name"
                                    value={reportName}
                                    onChange={(e) => setReportName(e.target.value)}
                                />
                            </InputRow>
                            <InputRow>
                                <VText>Start date</VText>
                                <SimpleDateSelector
                                    buttonText={fromDate !== null ? fromDate.toDateString().slice(4) : 'Start Date'}
                                    date={fromDate}
                                    setDate={setFromDate}
                                    showYearDropdown={true}
                                />
                            </InputRow>
                            <InputRow>
                                <VText>End date</VText>
                                <SimpleDateSelector
                                    buttonText={toDate !== null ? toDate.toDateString().slice(4) : 'End Date'}
                                    date={toDate}
                                    setDate={addDayToDate}
                                    showYearDropdown={true}
                                />
                            </InputRow>
                        </InputWrapper>
                        {errorMessage && <ErrorMessageContainer>{errorMessage}</ErrorMessageContainer>}
                        <ButtonWrapper>
                            <Button disabled={!allFieldsValid} type="submit" text="Order"></Button>
                        </ButtonWrapper>
                    </form>
                    {orderSubscriptionSuccess && reportPath && (
                        <VText>
                            Order sent successfully. In some minutes, you will find the report{' '}
                            <Link className="underline" to={reportPath}>
                                here.
                            </Link>
                        </VText>
                    )}
                    {orderSubscriptionSuccess === false && <VText>Order failed.</VText>}
                </div>
            )}
        </OrderReportWrapper>
    );
};

export default OrderPathfinderReport;
